import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import {
  CalendarPlus,
  Clock,
  Link as LinkIcon,
  Loader2,
  CheckCircle2,
  XCircle,
  UserX,
  RefreshCw,
  Save,
  PlayCircle,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../lib/api';

/**
 * MeetingsPanel — Système d'entretien minimaliste.
 *
 * - Lien externe collé manuellement (Google Meet / Zoom / Teams / autre).
 * - Notes libres + lien replay éditables.
 * - Décision post-entretien : Garder / Rejeter / No-show / Replanifier.
 * - Aucune génération automatique.
 */

const formatDateLabel = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const toLocalInputValue = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const defaultScheduledAt = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return toLocalInputValue(d);
};

const STATUS_BADGE = {
  scheduled: { label: 'Planifié', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  done: { label: 'Terminé', cls: 'bg-green-100 text-green-800 border-green-200' },
  no_show: { label: 'No-show', cls: 'bg-orange-100 text-orange-800 border-orange-200' },
  rescheduled: { label: 'Replanifié', cls: 'bg-purple-100 text-purple-800 border-purple-200' },
  cancelled: { label: 'Annulé', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
};

const OUTCOME_LABEL = {
  keep: '✅ Gardé → Talent Pool',
  reject: '🚫 Rejeté',
  no_show: '👻 No-show',
  reschedule: '🔁 Replanifié',
};

const MeetingsPanel = ({ candidateId, candidate, onCandidateRefresh }) => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // Schedule form
  const [scheduledAt, setScheduledAt] = useState(defaultScheduledAt());
  const [duration, setDuration] = useState(30);
  const [externalLink, setExternalLink] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchMeetings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/meetings/candidate/${candidateId}`);
      if (res.ok) {
        const data = await res.json();
        setMeetings(data.meetings || []);
      }
    } catch (_) {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  const resetForm = () => {
    setScheduledAt(defaultScheduledAt());
    setDuration(30);
    setExternalLink('');
    setNotes('');
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    if (!scheduledAt) {
      toast.error('Sélectionne une date et une heure');
      return;
    }
    setSubmitting(true);
    try {
      // Convert local-input string to ISO with timezone
      const iso = new Date(scheduledAt).toISOString();
      const res = await api.post('/admin/meetings/schedule', {
        candidate_id: candidateId,
        scheduled_at: iso,
        duration_minutes: Number(duration) || 30,
        external_link: externalLink.trim() || null,
        notes: notes.trim() || null,
      });
      if (res.ok) {
        toast.success('Entretien planifié');
        resetForm();
        setShowForm(false);
        await fetchMeetings();
        onCandidateRefresh?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors de la planification');
      }
    } catch (_) {
      toast.error('Erreur réseau');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="meetings-panel">
      <Card className="p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <CalendarPlus className="h-5 w-5 text-tp-accent" />
              Entretiens
            </h3>
            <p className="text-xs text-tp-text-muted mt-1">
              Lien externe (Google Meet / Zoom / Teams) collé manuellement
            </p>
          </div>
          {!showForm && (
            <Button
              size="sm"
              onClick={() => setShowForm(true)}
              data-testid="meeting-add-btn"
            >
              <CalendarPlus className="h-4 w-4 mr-1.5" />
              Planifier
            </Button>
          )}
        </div>

        {showForm && (
          <form
            onSubmit={handleSchedule}
            className="border border-tp-border rounded-lg p-4 bg-tp-bg space-y-3 mb-4"
            data-testid="meeting-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="meeting-when" className="text-xs">
                  Date et heure
                </Label>
                <Input
                  id="meeting-when"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  required
                  data-testid="meeting-when-input"
                />
              </div>
              <div>
                <Label htmlFor="meeting-duration" className="text-xs">
                  Durée (minutes)
                </Label>
                <Input
                  id="meeting-duration"
                  type="number"
                  min={5}
                  max={240}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  data-testid="meeting-duration-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="meeting-link" className="text-xs">
                Lien de la visioconférence (optionnel)
              </Label>
              <Input
                id="meeting-link"
                type="url"
                placeholder="https://meet.google.com/xxx-xxxx-xxx"
                value={externalLink}
                onChange={(e) => setExternalLink(e.target.value)}
                data-testid="meeting-link-input"
              />
              <p className="text-[11px] text-tp-text-muted mt-1">
                Colle ici l'URL Google Meet / Zoom / Teams. Le candidat la recevra par email.
              </p>
            </div>
            <div>
              <Label htmlFor="meeting-notes" className="text-xs">
                Notes internes (optionnel)
              </Label>
              <Textarea
                id="meeting-notes"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Points à creuser, objectif de l'entretien…"
                data-testid="meeting-notes-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                disabled={submitting}
              >
                Annuler
              </Button>
              <Button type="submit" size="sm" disabled={submitting} data-testid="meeting-submit-btn">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Envoi…
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-1.5" />
                    Planifier l'entretien
                  </>
                )}
              </Button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-tp-text-muted" />
          </div>
        ) : meetings.length === 0 ? (
          <p className="text-sm text-tp-text-muted py-6 text-center">
            Aucun entretien planifié pour ce candidat.
          </p>
        ) : (
          <ul className="space-y-3" data-testid="meetings-list">
            {meetings.map((m) => (
              <MeetingItem
                key={m.id}
                meeting={m}
                candidate={candidate}
                onChanged={async () => {
                  await fetchMeetings();
                  onCandidateRefresh?.();
                }}
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

const MeetingItem = ({ meeting, candidate, onChanged }) => {
  const [expanded, setExpanded] = useState(false);
  const [link, setLink] = useState(meeting.external_link || '');
  const [replay, setReplay] = useState(meeting.replay_url || '');
  const [notes, setNotes] = useState(meeting.notes || '');
  const [savingEdits, setSavingEdits] = useState(false);
  const [acting, setActing] = useState(null);

  // Reschedule UI
  const [rescheduleAt, setRescheduleAt] = useState('');

  const status = meeting.status || 'scheduled';
  const badge = STATUS_BADGE[status] || STATUS_BADGE.scheduled;
  const isOpen = status === 'scheduled' && !meeting.outcome;

  const handleSaveEdits = async () => {
    setSavingEdits(true);
    try {
      const res = await api.patch(`/admin/meetings/${meeting.id}`, {
        external_link: link,
        replay_url: replay,
        notes,
      });
      if (res.ok) {
        toast.success('Entretien mis à jour');
        await onChanged();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur de mise à jour');
      }
    } catch (_) {
      toast.error('Erreur réseau');
    } finally {
      setSavingEdits(false);
    }
  };

  const submitOutcome = async (outcome, extraNote = null) => {
    setActing(outcome);
    try {
      const payload = { outcome };
      if (extraNote) payload.note = extraNote;
      if (outcome === 'reschedule') {
        if (!rescheduleAt) {
          toast.error('Choisis un nouveau créneau');
          setActing(null);
          return;
        }
        payload.reschedule_at = new Date(rescheduleAt).toISOString();
      }
      const res = await api.post(`/admin/meetings/${meeting.id}/outcome`, payload);
      if (res.ok) {
        const map = {
          keep: 'Candidat passé en Talent Pool',
          reject: 'Candidat rejeté',
          no_show: 'No-show enregistré',
          reschedule: 'Entretien replanifié',
        };
        toast.success(map[outcome] || 'Décision enregistrée');
        setRescheduleAt('');
        await onChanged();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur');
      }
    } catch (_) {
      toast.error('Erreur réseau');
    } finally {
      setActing(null);
    }
  };

  return (
    <li
      className="border border-tp-border rounded-lg p-3 md:p-4 bg-white"
      data-testid={`meeting-item-${meeting.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Clock className="h-4 w-4 text-tp-text-muted" />
            <span className="font-medium text-sm">{formatDateLabel(meeting.scheduled_at)}</span>
            <span className="text-xs text-tp-text-muted">
              · {meeting.duration_minutes || 30} min
            </span>
            <Badge className={`border ${badge.cls}`}>{badge.label}</Badge>
            {meeting.outcome && (
              <Badge className="bg-tp-bg text-tp-text border border-tp-border">
                {OUTCOME_LABEL[meeting.outcome] || meeting.outcome}
              </Badge>
            )}
          </div>
          {meeting.external_link && (
            <div className="mt-2 text-sm">
              <a
                href={meeting.external_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-tp-accent hover:underline break-all"
                data-testid={`meeting-link-${meeting.id}`}
              >
                <LinkIcon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{meeting.external_link}</span>
                <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-60" />
              </a>
            </div>
          )}
          {meeting.replay_url && (
            <div className="mt-1 text-sm">
              <a
                href={meeting.replay_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-purple-700 hover:underline break-all"
              >
                <PlayCircle className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">Replay</span>
                <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-60" />
              </a>
            </div>
          )}
          {meeting.notes && !expanded && (
            <p className="text-xs text-tp-text-muted mt-2 line-clamp-2">{meeting.notes}</p>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setExpanded(!expanded)}
          data-testid={`meeting-toggle-${meeting.id}`}
        >
          {expanded ? 'Réduire' : 'Détails'}
        </Button>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-tp-border space-y-3">
          {/* Edit fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Lien visio</Label>
              <Input
                type="url"
                placeholder="https://meet.google.com/…"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                data-testid={`meeting-edit-link-${meeting.id}`}
              />
            </div>
            <div>
              <Label className="text-xs">Lien replay (post-call)</Label>
              <Input
                type="url"
                placeholder="https://drive.google.com/…"
                value={replay}
                onChange={(e) => setReplay(e.target.value)}
                data-testid={`meeting-edit-replay-${meeting.id}`}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Synthèse de l'entretien, points clés…"
              data-testid={`meeting-edit-notes-${meeting.id}`}
            />
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={handleSaveEdits}
              disabled={savingEdits}
              data-testid={`meeting-save-${meeting.id}`}
            >
              {savingEdits ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Sauvegarde…
                </>
              ) : (
                <>
                  <Save className="h-3.5 w-3.5 mr-1.5" />
                  Enregistrer
                </>
              )}
            </Button>
          </div>

          {/* Outcome actions */}
          {isOpen && (
            <div className="pt-3 border-t border-tp-border">
              <p className="text-xs font-medium text-tp-text mb-2">Décision post-entretien</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={!!acting}
                  onClick={() => submitOutcome('keep')}
                  data-testid={`meeting-outcome-keep-${meeting.id}`}
                >
                  {acting === 'keep' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Garder
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={!!acting}
                  onClick={() => submitOutcome('reject')}
                  data-testid={`meeting-outcome-reject-${meeting.id}`}
                >
                  {acting === 'reject' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 mr-1" />
                      Rejeter
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!!acting}
                  onClick={() => submitOutcome('no_show')}
                  data-testid={`meeting-outcome-noshow-${meeting.id}`}
                >
                  {acting === 'no_show' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <UserX className="h-4 w-4 mr-1" />
                      No-show
                    </>
                  )}
                </Button>
                <div className="flex flex-col gap-1">
                  <Input
                    type="datetime-local"
                    value={rescheduleAt}
                    onChange={(e) => setRescheduleAt(e.target.value)}
                    className="h-9 text-xs"
                    data-testid={`meeting-reschedule-input-${meeting.id}`}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!!acting || !rescheduleAt}
                    onClick={() => submitOutcome('reschedule')}
                    data-testid={`meeting-outcome-reschedule-${meeting.id}`}
                  >
                    {acting === 'reschedule' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Replanifier
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <p className="text-[11px] text-tp-text-muted mt-2">
                Garder → Talent Pool · Rejeter → Rejeté · No-show → À replanifier · Replanifier → nouveau créneau
              </p>
            </div>
          )}

          {meeting.outcome_note && (
            <div className="text-xs text-tp-text-muted bg-tp-bg p-2 rounded">
              <strong>Note post-entretien : </strong>
              {meeting.outcome_note}
            </div>
          )}
        </div>
      )}
    </li>
  );
};

export default MeetingsPanel;
